import { z } from 'zod';

/** Native still-image tracking overlays. No model, network or paid provider. */
export const TracyOptionsSchema = z.object({
  detection: z.enum(['luminance', 'key-color']).default('luminance'),
  keyColor: z.string().regex(/^#[0-9a-f]{6}$/i).default('#ffffff'),
  threshold: z.number().min(0).max(1).default(0.65),
  tolerance: z.number().min(0).max(1).default(0.2),
  minArea: z.number().min(0.0001).max(1).default(0.002),
  maxRegions: z.number().int().min(1).max(100).default(24),
  shape: z.enum(['rectangle', 'square', 'ellipse', 'circle']).default('rectangle'),
  fill: z.enum(['none', 'hatch', 'invert', 'random-patch', 'xray', 'duotone', 'glitch']).default('none'),
  marker: z.enum(['none', 'dot', 'plus', 'cross', 'polygon']).default('plus'),
  sides: z.number().int().min(3).max(12).default(4),
  markerFilled: z.boolean().default(false),
  rotation: z.number().min(-180).max(180).default(0),
  stretch: z.boolean().default(false),
  grid: z.enum(['none', 'edge', 'cartesian']).default('edge'),
  scale: z.enum(['pixels', 'percent']).default('pixels'),
  connection: z.enum(['none', 'spline', 'pcb', 'smooth', 'step']).default('pcb'),
  bend: z.number().min(0).max(1).default(0.5),
  balance: z.number().min(0).max(1).default(0.5),
  corner: z.number().min(0).max(1).default(0.5),
  label: z.enum(['none', 'coordinates', 'dimensions', 'area', 'id', 'hex', 'percent', 'matrix']).default('id'),
  font: z.enum(['monospace', 'sans-serif', 'serif']).default('monospace'),
  color: z.string().regex(/^#[0-9a-f]{6}$/i).default('#a4ffda'),
  lineWidth: z.number().min(0.5).max(8).default(1.5),
}).strict();
export type TracyOptions = z.infer<typeof TracyOptionsSchema>;
export const TRACY_DEFAULTS = TracyOptionsSchema.parse({});
export const TracyPresetSchema = z.object({ schemaVersion: z.literal(1), options: TracyOptionsSchema }).strict();
export const TRACY_PRESETS = {
  'Product scan': TRACY_DEFAULTS,
  'Editorial grid': TracyOptionsSchema.parse({ shape: 'circle', marker: 'dot', grid: 'cartesian', connection: 'spline', label: 'dimensions', color: '#ffffff' }),
  'Signal map': TracyOptionsSchema.parse({ fill: 'hatch', marker: 'polygon', sides: 6, connection: 'step', label: 'hex', color: '#ffbd69' }),
  'X-ray study': TracyOptionsSchema.parse({ fill: 'xray', grid: 'none', connection: 'smooth', label: 'matrix', color: '#8cddff' }),
};
export const TracyPreviewRequestSchema = z.object({
  canvasId: z.string().min(1).max(100), frameId: z.string().min(1).max(100), options: TracyOptionsSchema,
}).strict();
